/**
 * Light/dark mode + colour-palette manager.
 *
 *  - Mode (`light` | `dark`): defaults to system, persists once user picks.
 *  - Palette: a named colour scheme (Warmth, Ocean, Forest, …). Persists.
 *
 * Mode is applied via `data-theme` on <html>; palette via `data-palette`.
 * Existing CSS that targets `:root[data-theme="dark"]` keeps working.
 */

export type Theme = 'light' | 'dark';

export interface PaletteDef {
  id: string;
  label: string;
  /** Small swatch colour rendered next to the name in the dropdown. */
  swatch: string;
}

export const PALETTES: PaletteDef[] = [
  { id: 'warmth',   label: 'Warmth',   swatch: '#e07856' },
  { id: 'ocean',    label: 'Ocean',    swatch: '#3a86c8' },
  { id: 'forest',   label: 'Forest',   swatch: '#4a8d5e' },
  { id: 'plum',     label: 'Plum',     swatch: '#8a5aa8' },
  { id: 'sunset',   label: 'Sunset',   swatch: '#e8556d' },
  { id: 'mocha',    label: 'Mocha',    swatch: '#8b6750' },
  { id: 'citrus',   label: 'Citrus',   swatch: '#d8a82a' },
  { id: 'midnight', label: 'Midnight', swatch: '#5566aa' },
  { id: 'rose',     label: 'Rose',     swatch: '#d479a3' },
  { id: 'mint',     label: 'Mint',     swatch: '#3aa49a' },
  { id: 'slate',    label: 'Slate',    swatch: '#6b7a8c' },
];

const STORAGE_KEY = 'converter.theme.v1';
const PALETTE_KEY = 'converter.palette.v1';
const DEFAULT_PALETTE = 'warmth';

function readSaved(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

function readSavedPalette(): string {
  try {
    const v = localStorage.getItem(PALETTE_KEY);
    if (v && PALETTES.some(p => p.id === v)) return v;
  } catch { /* ignore */ }
  return DEFAULT_PALETTE;
}

function systemPrefers(): Theme {
  if (typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

function apply(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) {
    meta.content = theme === 'dark' ? '#231f1c' : '#fdf9f3';
  }
}

function applyPalette(id: string): void {
  document.documentElement.setAttribute('data-palette', id);
}

const listeners = new Set<(t: Theme) => void>();
const paletteListeners = new Set<(p: string) => void>();

export function getCurrentTheme(): Theme {
  return readSaved() ?? systemPrefers();
}

export function getCurrentPalette(): string {
  return readSavedPalette();
}

export function isUsingSystemPreference(): boolean {
  return readSaved() === null;
}

export function setTheme(theme: Theme): void {
  try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore */ }
  apply(theme);
  listeners.forEach(fn => fn(theme));
}

export function setPalette(id: string): void {
  if (!PALETTES.some(p => p.id === id)) return;
  try { localStorage.setItem(PALETTE_KEY, id); } catch { /* ignore */ }
  applyPalette(id);
  paletteListeners.forEach(fn => fn(id));
}

export function clearThemePreference(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  const t = systemPrefers();
  apply(t);
  listeners.forEach(fn => fn(t));
}

export function onThemeChange(fn: (t: Theme) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function onPaletteChange(fn: (p: string) => void): () => void {
  paletteListeners.add(fn);
  return () => { paletteListeners.delete(fn); };
}

/** Call once at boot. Applies the current theme + palette and watches system pref. */
export function initTheme(): void {
  apply(getCurrentTheme());
  applyPalette(getCurrentPalette());

  if (typeof matchMedia !== 'function') return;
  const mq = matchMedia('(prefers-color-scheme: dark)');
  const onChange = (): void => {
    if (isUsingSystemPreference()) {
      const next: Theme = mq.matches ? 'dark' : 'light';
      apply(next);
      listeners.forEach(fn => fn(next));
    }
  };
  if ('addEventListener' in mq) mq.addEventListener('change', onChange);
}
